import "@innovatrics/dot-document-auto-capture";
import "@innovatrics/dot-document-auto-capture/events";
import { DocumentCustomEvent } from "@innovatrics/dot-document-auto-capture/events";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import captureDocButton from "../../assets/captureDocButton.svg";
import Footer from "../../components/Footer";
import GoogleIcon from "../../components/GoogleIcon";
import CameraLoadingOverlay from "../../components/Loader/CameraLoadingOverlay";
import { countries, drivingLicenceName, idCardName, residentIDCardName } from "../../constants/countries";
import { documentSideConfigurationConstants } from "../../constants/documentSideConfigConstants.js";
import { UploadStatusMessages } from "../../constants/failedUpload";
import { languageData } from "../../constants/languageData";
import GlobalConfig from "../../context/GlobalConfig";
import JourneyConfig from "../../context/JourneyConfig";
import { arrayBufferToBase64 } from "../../utils/arrayBufferUtility.js";
import { getDocumentCountry, getDocumentIdType, getDocumentFieldName } from "../../utils/documentFields";
import { convertHeicToJpeg } from "../../utils/heicConverter";
import Head from "../Head";
import "./docCapture.css";
import DocumentCamera from "./DocumentCamera";
import DocumentCheck from "./DocumentCheck";
import DocumentUI from "./DocumentUI";
import FlipScreen from "./FlipScreen";
import MobileCapture from "./MobileCapture";

// Combined document capture that captures front first, then shows flip screen,
// then captures back, while keeping the DocumentCamera mounted throughout
// Then runs front-check and back-check phases before proceeding
const DocumentCapture = ({ stepperFunc, steps, currentDocumentStep, currentStep, frontImageRef, backImageRef, currentDocumentRetries }) => {
	const { config } = useContext(GlobalConfig);
	const { journey, setJourney, getJourney } = useContext(JourneyConfig);
	const color = config?.branding?.accentColor;
	const bgColor = "color-mix(in srgb, " + (config?.branding?.accentColor || "#1658aa") + " 10%, white)";
	const documentCapture = Array.isArray(config?.documentCapture) ? config?.documentCapture?.[currentDocumentStep] : config?.documentCapture;
	const [countryCode] = useState(() => getDocumentCountry(journey, currentDocumentStep));
	const [countryName] = useState(countryCode ? countries?.find((country) => country.value === countryCode)?.label : null);
	const [isFlipScreenProcessed, setIsFlipScreenProcessed] = useState(() => !!sessionStorage.getItem("isFlipScreenShown"));

	const { journeyId } = useParams();

	const innovatricsCameraRef = useRef(null);
	const streamRef = useRef(null);
	const [isAutoCaptureCameraRunning, setIsAutoCaptureCameraRunning] = useState(false);
	const [isSwitchingMode, setIsSwitchingMode] = useState(false);
	const timeoutRef = useRef(null);
	const [width, setWidth] = useState(window.innerWidth);
	const [facingMode] = useState("user");

	const hasCapturedFront = useRef(false);
	const hasCapturedBack = useRef(false);

	const [documentName] = useState(() => getDocumentIdType(journey, currentDocumentStep));

	const documentFailureReason = sessionStorage.getItem("documentFailureReason");
	const [sameSideDocumentFlagNumber] = useState(documentFailureReason ? (documentFailureReason === "ONLY_FRONT" ? 1 : documentFailureReason === "ONLY_BACK" ? 2 : -1) : -1);

	// Phases: 'front' -> 'flip' -> 'back' -> 'front-check' -> 'back-check'
	const [phase, setPhase] = useState(documentFailureReason ? (documentFailureReason === "ONLY_FRONT" ? "back" : "front") : "front");
	const [retakeTarget, setRetakeTarget] = useState(documentFailureReason === "ONLY_FRONT" ? "back" : documentFailureReason === "ONLY_BACK" ? "front" : null); // 'front' | 'back' | null
	const [documentSideConfiguration, setDocumentSideConfiguration] = useState(
		config?.documentCapture?.[currentDocumentStep]?.documentSideConfiguration ?? documentSideConfigurationConstants.FRONT_MANDATORY_BACK_OPTIONAL
	);
	// Documents whose back side carries nothing worth capturing. Both inputs below belong to the
	// document being captured right now — a passport in the first slot must not decide that a
	// second document from another country needs no back side (IDX-677).
	const rules = [
		{ type: "Passport" },
		{ type: "PAN" },
		{ type: "Driving License", allowedCountries: ["IN", "AU"] }
	];


	const isAllowedDocument = rules.some(rule => {
		if (!documentName?.includes(rule.type)) return false;
		if (rule.allowedCountries) {
			return rule.allowedCountries.includes(countryCode);
		}
		return true;
	});

	const onlyFrontJourney = isAllowedDocument || documentSideConfiguration === documentSideConfigurationConstants.FRONT || documentFailureReason === "ONLY_BACK";

	// Manual/Upload support
	const [captureMode, setCaptureMode] = useState("auto");
	const fileInputRef = useRef(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [message, setMessage] = useState("");
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const prevCaptureModeRef = useRef(captureMode);
	const initialTimeRef = useRef(performance.now());
	const [frontAutoCaptureLoadingTime, setFrontAutoCaptureLoadingTime] = useState("NA");
	const [backAutoCaptureLoadingTime, setBackAutoCaptureLoadingTime] = useState("NA");
	const frontCaptureModeRef = useRef(null);
	const backCaptureModeRef = useRef(null);
	const [disableBackButton, setDisableBackButton] = useState(false);
	const autoCaptureCameraDivRef = useRef(null);

	const language = sessionStorage.getItem("language") ?? config?.branding?.language ?? "en";
	const [appState, setAppState] = useState("");

	useEffect(() => {
		setDocumentSideConfiguration(config?.documentCapture?.[currentDocumentStep]?.documentSideConfiguration ?? documentSideConfigurationConstants.FRONT_MANDATORY_BACK_OPTIONAL);
	}, [config, currentDocumentStep]);

	useEffect(() => {
		const handleResize = () => setWidth(window.innerWidth);
		window.addEventListener("resize", handleResize);
		const handleStateChanged = (event) => {
			if (!event?.detail) return;
			if (event?.detail?.appState === "RUNNING") {
				if (innovatricsCameraRef.current) {
					streamRef.current = innovatricsCameraRef.current?.getVideoElement();
				}
				setIsAutoCaptureCameraRunning(true);
				if (timeoutRef.current) clearTimeout(timeoutRef.current);
			} else if (event?.detail?.appState === "ERROR") {
				setIsAutoCaptureCameraRunning(true);
			}
		};

		document.addEventListener("document-auto-capture:state-changed", handleStateChanged);

		const handleAppState = (event) => {
			setAppState(event?.detail?.appState);
		};
		document.addEventListener(DocumentCustomEvent.STATE_CHANGED, handleAppState);

		return () => {
			window.removeEventListener("resize", handleResize);
			document.removeEventListener("document-auto-capture:state-changed", handleStateChanged);
			document.removeEventListener(DocumentCustomEvent.STATE_CHANGED, handleAppState);
		};
	}, []);

	useEffect(() => {
		if (journeyId && !journey) getJourney(journeyId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [journeyId]);

	// Camera readiness listener and fallback
	useEffect(() => {
		timeoutRef.current = setTimeout(() => {
			if (!isAutoCaptureCameraRunning) {
				setIsAutoCaptureCameraRunning(true);
			}
		}, 5000);

		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [isAutoCaptureCameraRunning]);

	useEffect(() => {
		if (captureMode === "auto" && (phase === "front" || phase === "back") && autoCaptureCameraDivRef.current && isAutoCaptureCameraRunning && initialTimeRef.current !== -1 && appState === "RUNNING") {
			const durationInSeconds = (performance.now() - initialTimeRef.current) / 1000;
			if (phase === "front") {
				setFrontAutoCaptureLoadingTime(durationInSeconds.toFixed(2));
			} else if (phase === "back") {
				setBackAutoCaptureLoadingTime(durationInSeconds.toFixed(2));
			}
			initialTimeRef.current = -1;
		}
	}, [phase, isAutoCaptureCameraRunning, captureMode, appState]);

	useEffect(() => {
		if (captureMode === "auto" && (phase === "front" || phase === "back") && autoCaptureCameraDivRef.current && appState !== "RUNNING" && appState !== "WAITING") {
			setIsAutoCaptureCameraRunning(false);
			if (initialTimeRef.current === -1) {
				initialTimeRef.current = performance.now();
			}
		}
	}, [captureMode, phase, appState]);

	// When flip screen is processed, move to back capture phase
	useEffect(() => {
		if (phase === "flip" && isFlipScreenProcessed) {
			document.dispatchEvent(
				new CustomEvent("document-auto-capture:control", {
					detail: { instruction: "continue-detection" }
				})
			);
			setPhase("back");
		}
	}, [phase, isFlipScreenProcessed]);

	// While on flip screen (not processed), pause Innovatrics detection to prevent hidden takePhoto attempts
	useEffect(() => {
		if (phase === "flip" && !isFlipScreenProcessed) {
			try {
				document.dispatchEvent(
					new CustomEvent("document-auto-capture:control", {
						detail: { instruction: "pause-detection" }
					})
				);
			} catch (_) {}
		}
	}, [phase, isFlipScreenProcessed]);

	const fileToBase64 = (file) =>
		new Promise((resolve, reject) => {
			try {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result);
				reader.onerror = reject;
				reader.readAsDataURL(file);
			} catch (e) {
				reject(e);
			}
		});

	const handleDocumentPhotoTaken = useCallback(
		async ({ image }) => {
			if (!image) return;
			try {
				if (phase === "front") {
					const base64 = await fileToBase64(image);
					frontImageRef.current = base64;
					frontCaptureModeRef.current = "auto";
					if (retakeTarget === "front" || hasCapturedBack.current) {
						setRetakeTarget(null);
						setPhase("front-check");
					} else {
						if (onlyFrontJourney) {
							setPhase("front-check");
						} else {
							// Show flip screen; keep camera mounted
							setPhase("flip");
						}
					}
				} else if (phase === "back") {
					const base64 = await fileToBase64(image);
					backImageRef.current = base64;
					backCaptureModeRef.current = "auto";
					if (retakeTarget === "back") {
						setRetakeTarget(null);
						setPhase("back-check");
					} else {
						setPhase("front-check");
					}
				}
			} catch (e) {
				// Swallow errors and allow re-capture
				console.error("[DocumentCapture] Error handling photo:", e);
			}
		},
		[phase, hasCapturedFront, hasCapturedBack, , backImageRef, stepperFunc, steps, retakeTarget, documentSideConfiguration, sameSideDocumentFlagNumber]
	);

	// Safe stop for manual media stream
	const stopSafeStreamFromRef = useCallback(() => {
		try {
			const stream = streamRef?.current;
			if (!stream) return;
			const tracks = stream.getTracks();
			tracks.forEach((track) => track.stop());
			streamRef.current = null;
			if (videoRef?.current?.srcObject) {
				videoRef.current.srcObject = null;
			}
		} catch (e) {
			console.error("[DocumentCapture][safeStream] Stream cleanup failed:", e);
		}
	}, []);

	useEffect(() => {
		return () => {
			stopSafeStreamFromRef();
		};
	}, [stopSafeStreamFromRef]);

	// Start manual camera when switching to manual mode
	useEffect(() => {
		async function startCamera() {
			try {
				setIsSwitchingMode(true);
				stopSafeStreamFromRef();
				const videoConstraints = { facingMode };
				const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, zoom: false });
				streamRef.current = stream;
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					videoRef.current.onloadedmetadata = async () => {
						try {
							videoRef.current.muted = true; // important for iOS
							await videoRef.current.play?.(); // actually start playback
						} catch {}
						finally {
							setIsSwitchingMode(false);
						}
					};
				}
			} catch (error) {
				console.error("[DocumentCapture] Error accessing camera:", error);
			}
		}

		if (captureMode === "manual") {
			//Pause Innovatrics detection and stop its camera stream
			try {
				document.dispatchEvent(
					new CustomEvent("document-auto-capture:control", {
						detail: { instruction: "pause-detection" }
					})
				);
			} catch (_) {}
			startCamera();
			setAppState("");
		} else {
			// Resume detection when returning to auto mode
			// If we are switching from manual -> auto, stop the manual stream first
			if (prevCaptureModeRef.current === "manual") {
				stopSafeStreamFromRef();
			}
			try {
				document.dispatchEvent(
					new CustomEvent("document-auto-capture:control", {
						detail: { instruction: "continue-detection" }
					})
				);
			} catch (_) {}
			// Show overlay until Innovatrics reports RUNNING again
		}
		prevCaptureModeRef.current = captureMode;
	}, [captureMode, stopSafeStreamFromRef]);

	// Reset capture mode to auto on ANY phase change
	useEffect(() => {
		if (captureMode !== "auto") {
			setCaptureMode("auto");
		}
	}, [phase]);

	const captureImage = useCallback(() => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (!video || !canvas) return;
		if (!video.videoWidth || !video.videoHeight) {
			console.warn("[DocumentCapture][Manual Capture] Video metadata not ready; skipping capture");
			return;
		}
		const context = canvas.getContext("2d");
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		context.drawImage(video, 0, 0, canvas.width, canvas.height);
		canvas.toBlob((blob) => {
			const reader = new FileReader();
			reader.onload = async () => {
				const base64Data = reader.result;
				try {
					if (phase === "front") {
						frontImageRef.current = base64Data;
						frontCaptureModeRef.current = "manual";
						if (retakeTarget === "front" || hasCapturedBack.current) {
							setRetakeTarget(null);
							setPhase("front-check");
						} else {
							if (onlyFrontJourney) {
								setPhase("front-check");
							} else {
								setPhase("flip");
							}
						}
					} else if (phase === "back") {
						backImageRef.current = base64Data;
						backCaptureModeRef.current = "manual";
						if (retakeTarget === "back") {
							setRetakeTarget(null);
							setPhase("back-check");
						} else {
							setPhase("front-check");
						}
					}
				} catch (e) {
					console.error("[DocumentCapture][Manual Capture] Error:", e);
				}
			};
			reader.readAsDataURL(blob);
		}, "image/png");
	}, [phase, frontImageRef, backImageRef, stepperFunc, steps, retakeTarget, documentSideConfiguration, sameSideDocumentFlagNumber]);

	const handleClose = () => setIsModalOpen(false);

	const handleFileChange = async (e) => {
		try {
			const file = e.target.files[0];
			if (!file) return;

			// India supports PDF uploads, same as other IDs.
			const allowedExtensions = ["image/jpeg", "image/png", "image/jpg", "image/heic", "image/heif", "application/pdf"];
			if (!allowedExtensions.includes(file.type)) {
				setMessage(languageData[language]?.failedUpload?.text || UploadStatusMessages.FailedUploadText);
				setIsModalOpen(true);
				e.target.value = "";
				return;
			}

			// Normalize file (convert HEIC/HEIF to JPEG), then apply uniform size check and base64 conversion
			const maxSize = 5 * 1024 * 1024; // 5 MB
			const isHeic = file.type.includes('heic') || file.type.includes('heif');
			const normalizedFile = isHeic ? await convertHeicToJpeg(file) : file;


			const buffer = await normalizedFile.arrayBuffer();
			if (buffer.byteLength > maxSize) {
				setMessage(languageData[language]?.failedUpload?.text || UploadStatusMessages.FailedUploadText);
				setIsModalOpen(true);
				e.target.value = "";
				return;
			}

			const base64Data = `data:${normalizedFile.type};base64,${arrayBufferToBase64(buffer)}`;
			e.target.value = "";

			if (phase === "front") {
				frontImageRef.current = base64Data;
				frontCaptureModeRef.current = "upload";
				if (retakeTarget === "front" || hasCapturedBack.current) {
					setRetakeTarget(null);
					setPhase("front-check");
				} else {
					if (onlyFrontJourney) {
						setPhase("front-check");
					} else {
						setPhase("flip");
					}
				}
			} else {
				backImageRef.current = base64Data;
				backCaptureModeRef.current = "upload";
				if (retakeTarget === "back") {
					setRetakeTarget(null);
					setPhase("back-check");
				} else {
					setPhase("front-check");
				}
			}
		} catch (error) {
			console.error("[DocumentCapture][File Upload] Unexpected error:", error);
			e.target.value = "";
		}
	};

	return (
		<>
			{/* Back leads to this document's selection screen, so it is hidden only when this
			    document's type arrived prefilled — there is no screen behind it then. The field is
			    per document (idType / idType2 / idType3), so a journey that prefills only document
			    1 must still offer Back on documents 2 and 3. */}
			<Head
				countryCode={countryCode}
				showBackIcon={!journey?.userInfo?.id?.[getDocumentFieldName("idType", currentDocumentStep)]}
				showLanguages={false}
				style={{ zIndex: "100", position: "relative", top: 0 }}
				stepperFunc={stepperFunc}
				handleBack={() => {
					if (!disableBackButton) {
						if (phase === "back-check") {
							setPhase("front-check");
						} else {
							if (frontImageRef.current) {
								frontImageRef.current = null;
								frontCaptureModeRef.current = null;
							}
							if (backImageRef.current) {
								backImageRef.current = null;
								backCaptureModeRef.current = null;
							}
							sessionStorage.removeItem("isFlipScreenShown");
							setJourney({
								...journey,
								capturedData: { ...journey?.capturedData, [getDocumentFieldName("idType", currentDocumentStep)]: "" }
							});
							stepperFunc(steps[2]);
						}
					}
				}}
			/>
			<div className="parent-container" style={{ paddingTop: 0, display: phase === "flip" || phase === "front-check" || phase === "back-check" ? "none" : null }}>
				<div className="main">
					<div className="left-section" style={{ flexDirection: "column", background: "black" }}>
						<CameraLoadingOverlay isVisible={!isAutoCaptureCameraRunning} />
						<div style={{ position: "relative", marginBottom: 40, display: phase === "flip" || phase === "front-check" || phase === "back-check" ? "none" : null }} className="auto-capture">
							<div className="video-container" style={{ width: "100%", height: width <= 767 ? "100%" : "auto", overflow: "hidden", position: "relative" }}>
								{/* Mount Innovatrics camera only in auto mode and visible phases */}
								{captureMode === "auto" && (
									<div ref={autoCaptureCameraDivRef}>
										<DocumentCamera ref={innovatricsCameraRef} cameraFacing={facingMode} onPhotoTaken={handleDocumentPhotoTaken} placeholder="id-solid" backdropColor="none" />
										<DocumentUI
											theme={{ colors: { placeholderColor: "white", instructionColorSuccess: "#00FF83" } }}
											placeholder="id-solid"
											backdropColor="rgba(10, 10, 10, 0.5)"
											instructions={{}}
											appStateInstructions={{ loading: { text: "", visible: false } }}
										/>
									</div>
								)}

								{/* Manual capture container (render only in manual) */}
								{captureMode === "manual" && (
									<div style={{ position: "relative", marginBottom: 40 }} className="auto-capture">
										<div className="video-container" style={{ width: "100%", height: width <= 767 ? "100%" : "auto", overflow: "hidden", marginBottom: 20 }}>
											<video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "auto", objectFit: "cover", transform: "scaleX(-1)" }} />
											<canvas ref={canvasRef} style={{ display: "none" }} />
										</div>
										<div style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
											<div style={{ borderColor: "white", borderWidth: 2, height: 200, width: 300, marginBottom: "20px", borderRadius: "16px" }}></div>
										</div>
									</div>
								)}
							</div>
						</div>

						{width > 767 &&
							(captureMode === "auto" ? (
								<div style={{ fontSize: 14, fontWeight: 500, textAlign: "center", position: "absolute", bottom: "40px", width: "100%" }}>
									<div style={{ color: "#919191" }}>{languageData[language]?.mobileCapture?.stillNotWorking ?? "Still not working?"}</div>
									<div style={{ textDecorationLine: "underline", color: "white", cursor: "pointer" }} onClick={() => setCaptureMode("manual")}>
										{languageData[language]?.mobileCapture?.tryClickToCapture ?? "Try click-to-capture instead"}
									</div>
								</div>
							) : (
								<div onClick={() => captureImage()} style={{ position: "absolute", bottom: 24, cursor: "pointer" }}>
									<img src={captureDocButton} alt="button_link" />
								</div>
							))}
					</div>

					<div className="right-section md:items-start items-center" style={{ paddingTop: width <= 767 ? "18px" : "32px", fontFamily: "Inter, sans-serif" }}>
						{phase !== "flip" &&
							phase !== "front-check" &&
							phase !== "back-check" &&
							(width > 767 ? (
								<div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
									<div>
										<div className="flex flex-col md:mt-[80px]">
											<div className="flex justify-center md:justify-start">
												<svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
													<circle cx="22" cy="22" r="22" fill={bgColor} style={{ background: bgColor }} />
													<g clipPath="url(#clip0_1556_28470)">
														<path
															d="M15 25.5H13V31.5H19V29.5H15V25.5ZM15 15.5H19V13.5H13V19.5H15V15.5ZM31 13.5H25V15.5H29V19.5H31V13.5ZM29 29.5H25V31.5H31V25.5H29V29.5ZM22 18.5C19.79 18.5 18 20.29 18 22.5C18 24.71 19.79 26.5 22 26.5C24.21 26.5 26 24.71 26 22.5C26 20.29 24.21 18.5 22 18.5ZM22 24.5C20.9 24.5 20 23.6 20 22.5C20 21.4 20.9 20.5 22 20.5C23.1 20.5 24 21.4 24 22.5C24 23.6 23.1 24.5 22 24.5Z"
															fill="#1658aa"
															style={{ fill: color }}
														/>
													</g>
													<defs>
														<clipPath id="clip0_1556_28470">
															<rect width="24" height="24" fill="white" transform="translate(10 10.5)" />
														</clipPath>
													</defs>
												</svg>
											</div>
											<div className="mt-6 md:mt-4 text-[18px] md:text-[26px] font-semibold text-[#1658aa] text-center md:text-left mb-6 md:mb-0" style={{ color: color }}>
												{phase === "front"
													? (sameSideDocumentFlagNumber === 1 ? languageData[language]?.backDocCapture?.heading : languageData[language]?.frontDocCapture?.heading) +
													" " +
													(documentName == "Resident Id Card"
														? residentIDCardName?.[countryCode]
															? residentIDCardName?.[countryCode]
															: "Identity Card"
														: documentName == "Passport"
															? `${countryName} Passport`
															: documentName == "Driving License"
																? drivingLicenceName?.[countryCode]
																	? drivingLicenceName?.[countryCode]
																	: documentName
																: documentName == "Id Card"
																	? idCardName?.[countryCode]
																		? idCardName?.[countryCode]
																		: documentName
																	: documentName) +
													" " +
													(languageData[language]?.frontDocCapture?.headingBack ? languageData[language]?.frontDocCapture?.headingBack : languageData["en"]?.frontDocCapture?.headingBack)
													: languageData[language]?.backDocCapture?.heading +
													" " +
													(documentName == "Resident Id Card"
														? residentIDCardName?.[countryCode]
															? residentIDCardName?.[countryCode]
															: "Identity Card"
														: documentName == "Passport"
															? `${countryName} Passport`
															: documentName == "Driving License"
																? drivingLicenceName?.[countryCode]
																	? drivingLicenceName?.[countryCode]
																	: documentName
																: documentName == "Id Card"
																	? idCardName?.[countryCode]
																		? idCardName?.[countryCode]
																		: documentName
																	: documentName) +
													" " +
													(languageData[language]?.frontDocCapture?.headingBack ? languageData[language]?.frontDocCapture?.headingBack : languageData["en"]?.frontDocCapture?.headingBack)}
											</div>
										</div>

										{phase === "front" && documentCapture?.uploadAllowed && (
											<div className="flex flex-col md:justify-end items-center md:gap-[12px] w-full md:mt-24">
												<div style={{ color: "#919191", fontSize: 12, fontWeight: 500 }}>
													{"jpeg, jpg, png, heic, heif, pdf under 5 mb"}
												</div>
												<button
													className="flex items-center justify-center  gap-[8px] w-full pt-[12px] pb-[12px] pr-[24px] pl-[24px] text-white rounded-[4px] text-[14px] font-medium mt-3 md:mt-[0px]"
													style={{ borderColor: color, borderWidth: 1, color: color }}
													onClick={() => fileInputRef.current?.click()}
												>
													{languageData[language]?.frontDocCapture?.uploadButton ?? "or, Upload ID"} <GoogleIcon iconName="upload" size={18} style={{ color: color }} />
												</button>
											</div>
										)}

										{phase === "back" ? (
											<div className="w-full md:mt-[30px]">
												{documentCapture?.uploadAllowed && (
													<div
														className={`flex flex-col md:justify-end items-center md:gap-[12px] w-full ${(documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY || documentFailureReason === "ONLY_FRONT") ? "md:mt-16" : "md:mt-2"
															}`}
													>
														<div style={{ color: "#919191", fontSize: 12, fontWeight: 500 }}>
															{"jpeg, jpg, png, heic, heif, pdf under 5 mb"}
														</div>
														<button
															className="flex items-center justify-center  gap-[8px] w-full pt-[12px] pb-[12px] pr-[24px] pl-[24px] text-white rounded-[4px] text-[14px] font-medium mt-3 md:mt-[0px]"
															style={{ borderColor: color, borderWidth: 1, color: color }}
															onClick={() => fileInputRef.current?.click()}
														>
															{languageData[language]?.backDocCapture?.uploadButton ?? "or, Upload ID"} <GoogleIcon iconName="upload" size={18} style={{ color: color }} />
														</button>
													</div>
												)}
												<div className={`${(documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY || documentFailureReason === "ONLY_FRONT") ? "" : "md:mt-[12px]"}`}>
													<button
														onClick={() => {
															if (hasCapturedFront.current) {
																stepperFunc(steps[0]);
															} else {
																setPhase("front-check");
															}
														}}
														className="flex flex-row items-center justify-center md:gap-4 gap-3 w-full text-white py-4 px-4 rounded mt-3 md:mt-[0px] "
														style={{
															backgroundColor: appState ? color : bgColor,
															display: (documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY || documentFailureReason === "ONLY_FRONT") ? "none" : "flex"
														}}
														disabled={!appState}
													>
														{languageData[language]?.backDocCapture?.skipButton ?? languageData["en"]?.backDocCapture?.skipButton ?? "Skip"}
													</button>
												</div>
											</div>
										) : null}
										<Footer />
									</div>
								</div>
							) : (
								<MobileCapture
									currentDocumentStep={currentDocumentStep}
									uploadAllowed={documentCapture?.uploadAllowed}
									color={color}
									journey={journey}
									isSwitchingMode={isSwitchingMode}
									isAutoCaptureCameraRunning={isAutoCaptureCameraRunning}
									docType={phase === "front" ? "front" : "back"}
									captureMode={captureMode}
									switchToManual={() => setCaptureMode("manual")}
									captureClick={() => captureImage()}
									uploadClick={() => fileInputRef.current?.click()}
									skipClick={() => {
										if (hasCapturedFront.current) {
											stepperFunc(steps[0]);
										} else {
											setPhase("front-check");
										}
									}}
									sameSideDocumentFlagNumber={sameSideDocumentFlagNumber}
									documentSideConfiguration={documentSideConfiguration}
								/>
							))}
					</div>
				</div>
			</div>

			{phase === "flip" && <FlipScreen setIsFlipScreenProcessed={setIsFlipScreenProcessed} stepperFunc={stepperFunc} steps={steps} hideHeader={true} hideContinueBtn={true} />}

			{/* Needed for integrating new DocumentCheck.jsx */}
			{phase === "front-check" && (
				<DocumentCheck
					image={frontImageRef.current}
					phase="front-check"
					stepperFunc={stepperFunc}
					steps={steps}
					isFirstDocumentVerificationDone={false}
					currentDocumentStep={currentDocumentStep}
					currentStep={currentStep}
					preventBack={setDisableBackButton}
					capturingMode={frontCaptureModeRef.current}
					currentDocumentRetries={currentDocumentRetries}
					autoCaptureLoadingTime={frontAutoCaptureLoadingTime}
					onRetake={() => {
						setRetakeTarget("front");
						frontImageRef.current = null;
						frontCaptureModeRef.current = null;
						hasCapturedFront.current = false;
						setPhase("front");
						document.dispatchEvent(
							new CustomEvent("document-auto-capture:control", {
								detail: { instruction: "continue-detection" }
							})
						);
					}}
					onConfirm={() => {
						hasCapturedFront.current = true;
						if (backImageRef.current && !onlyFrontJourney) {
							setPhase("back-check");
						} else {
							// No back captured (skipped). Proceed similar to previous skip flow
							stepperFunc(steps[0]);
						}
					}}
				/>
			)}
			{/* Needed for integrating new DocumentCheck.jsx */}
			{phase === "back-check" && (
				<DocumentCheck
					image={backImageRef.current}
					phase="back-check"
					stepperFunc={stepperFunc}
					steps={steps}
					isFirstDocumentVerificationDone={false}
					currentDocumentStep={currentDocumentStep}
					currentStep={currentStep}
					preventBack={setDisableBackButton}
					capturingMode={backCaptureModeRef.current}
					currentDocumentRetries={currentDocumentRetries}
					autoCaptureLoadingTime={backAutoCaptureLoadingTime}
					onRetake={() => {
						setRetakeTarget("back");
						backImageRef.current = null;
						backCaptureModeRef.current = null;
						hasCapturedBack.current = false;
						setPhase("back");
						document.dispatchEvent(
							new CustomEvent("document-auto-capture:control", {
								detail: { instruction: "continue-detection" }
							})
						);
					}}
					onConfirm={() => {
						hasCapturedBack.current = true;
						stepperFunc(steps[0]);
					}}
				/>
			)}

			{/* upload file input */}
			<div style={{ position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0 }}>
				<input type="file" ref={fileInputRef} accept=".jpg, .png, .jpeg, .heic, .heif, .pdf" onChange={handleFileChange} />
			</div>

			{isModalOpen && (
				<div className="modal">
					<div className="modal-content">
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
							<GoogleIcon iconName="cancel" size={40} style={{ color: "#F14437" }} className="mb-4" />
						</div>
						<div>{languageData[language]?.failedUpload?.title || UploadStatusMessages.FailedUploadTitle}</div>
						<p className="mt-4">{message}</p>
						<button
							className="px-8 py-4 mt-12 md:mt-20"
							style={{
								border: "1px solid rgba(0, 0, 0, 0.10)",
								position: "relative",
								borderRadius: 4,
								width: "100%",
								display: "flex",
								alignItems: "center",
								flexDirection: "row",
								justifyContent: "center",
								background: color,
								color: "white"
							}}
							onClick={handleClose}
						>
							{languageData[language]?.failedUpload?.btn || UploadStatusMessages.FailedUploadButton}
						</button>
					</div>
				</div>
			)}
		</>
	);
};

export default DocumentCapture;
